import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import DateRangeSelector from "../../common/components/DateRangeSelector";
import PageHeader from "../../common/components/PageHeader";
import { toast } from "../../common/components/Toast";
import { getFinancials } from "../api/financials";
import PayoutSummaryCard from "../components/PayoutSummaryCard";
import ProducerBalancesTable from "../components/ProducerBalancesTable";
import RevenueByTitleTable from "../components/RevenueByTitleTable";
import RevenueWaterfallCard from "../components/RevenueWaterfallCard";

function Financials() {
  const { t } = useTranslation("financials");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProducerId, setSelectedProducerId] = useState("");
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const periodStart = params.get("period_start");
    const periodEnd = params.get("period_end");

    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getFinancials({
          period_start: periodStart,
          period_end: periodEnd,
        });
        setData(result);
        setSelectedProducerId("");
      } catch (error) {
        toast.error(error.message || t("error"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [location.search, t]);

  const filteredProducers = useMemo(() => {
    if (!data?.producer_balances) return [];
    if (!selectedProducerId) return data.producer_balances;
    return data.producer_balances.filter(
      (p) => p.user_id === selectedProducerId,
    );
  }, [data, selectedProducerId]);

  const filteredSummary = useMemo(() => {
    if (!data?.payout_summary) return null;
    if (!selectedProducerId) return data.payout_summary;
    const producer = data.producer_balances?.find(
      (p) => p.user_id === selectedProducerId,
    );
    if (!producer) return data.payout_summary;
    return {
      total_paid_out: producer.total_paid,
      total_outstanding: Math.max(producer.remaining, 0),
      producer_count: 1,
    };
  }, [data, selectedProducerId]);

  const filteredTitles = useMemo(() => {
    if (!data?.revenue_by_title) return [];
    if (!selectedProducerId) return data.revenue_by_title;
    const producer = data.producer_balances?.find(
      (p) => p.user_id === selectedProducerId,
    );
    if (!producer) return data.revenue_by_title;
    const producerAssetIds = new Set(
      producer.assets?.map((a) => a.asset_id) || [],
    );
    return data.revenue_by_title.filter((t) =>
      producerAssetIds.has(t.asset_id),
    );
  }, [data, selectedProducerId]);

  return (
    <>
      <PageHeader
        title={t("title")}
        description={t("description")}
        appendActions={
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {data?.producer_balances?.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>{t("filter.producer")}</InputLabel>
                <Select
                  value={selectedProducerId}
                  label={t("filter.producer")}
                  onChange={(e) => setSelectedProducerId(e.target.value)}
                >
                  <MenuItem value="">{t("filter.allProducers")}</MenuItem>
                  {data.producer_balances.map((p) => (
                    <MenuItem key={p.user_id} value={p.user_id}>
                      {p.user_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <DateRangeSelector />
          </Box>
        }
      />

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <Spinner animation="border" />
        </Box>
      ) : (
        data && (
          <>
            <PayoutSummaryCard
              summary={filteredSummary}
              waterfall={data.waterfall}
            />
            <RevenueWaterfallCard waterfall={data.waterfall} />
            <ProducerBalancesTable producers={filteredProducers} />
            <RevenueByTitleTable titles={filteredTitles} />
          </>
        )
      )}
    </>
  );
}

export default Financials;
